import { ReactElement } from 'react';
import { ReactFlowInstance } from 'reactflow';

import { setContinueRegistry } from '@components/generic-modals';
import { HIDE_CONTAINER_HEADER_CLASSNAME } from '@components/hide-container/hide-container';
import { SidebarReadOnlyContext, showSidebar } from '@components/sidebar';

import { getJourneyActionQueryKey } from 'api/journey-actions/hooks';
import { queryClient } from 'api/query-client';
import { TEMPLATE_PREVIEW_BUTTON_CLASSNAME } from 'ui/actions/common';

import { JourneyNodeBase } from './node-types/types';

type SidebarComponent = (props: { journeyActionId: string }) => ReactElement;
export type SidebarContentMap = Record<string, SidebarComponent>;

let sidebarContentMapRef: SidebarContentMap | null = null;
let reactFlowInstanceRef: ReactFlowInstance | null = null;

export const registerSidebarContentMap = (map: SidebarContentMap) => {
  sidebarContentMapRef = map;
};

export const registerReactFlowInstance = (instance: ReactFlowInstance) => {
  reactFlowInstanceRef = instance;
};

export const getReactFlowInstance = () => reactFlowInstanceRef;

export const FOCUS_NODE_DURATION = 1000;
export const FOCUS_NODE_MAX_ZOOM = 1;
export const FOCUS_NODE_PADDING = 0.5;

export const showActionSidebar = (
  node: JourneyNodeBase,
  options?: { isNewAction?: boolean; focusNode?: boolean }
) => {
  if (!sidebarContentMapRef) {
    console.error('Sidebar content map not registered');
    return;
  }

  const hasSidebar = node.type in sidebarContentMapRef;
  if (hasSidebar) {
    if (options?.focusNode && reactFlowInstanceRef) {
      reactFlowInstanceRef.fitView({
        nodes: [{ id: node.id }],
        duration: FOCUS_NODE_DURATION,
        maxZoom: FOCUS_NODE_MAX_ZOOM,
        padding: FOCUS_NODE_PADDING,
      });
    }
    const SidebarComponent = sidebarContentMapRef[node.type];
    setContinueRegistry({ onContinue: () => showActionSidebar(node), id: node.id });
    queryClient.removeQueries(getJourneyActionQueryKey(node.id));
    showSidebar({
      component: SidebarComponent,
      options: {
        variant: 'REGULAR',
        id: node.id,
        onHide: () => {
          setTimeout(() => {
            queryClient.invalidateQueries({
              predicate: (query) =>
                query.queryKey[0] === 'journey' && query.queryKey[1] !== 'action',
            });
            // let closing animation finish before invalidating queries
          }, 350);
        },
      },
      props: { journeyActionId: node.id, ...(options?.isNewAction ? { isNewAction: true } : {}) },
    });
  }
};

/**
 * Show action sidebar in read-only mode for version preview.
 * Similar to showTemplateActionSidebar but for preview mode.
 */
export const showPreviewActionSidebar = (node: JourneyNodeBase) => {
  if (!sidebarContentMapRef) {
    console.error('Sidebar content map not registered');
    return;
  }

  const hasSidebar = node.type in sidebarContentMapRef;
  if (hasSidebar) {
    const SidebarComponent = sidebarContentMapRef[node.type];
    showSidebar({
      component: (props) => (
        <SidebarReadOnlyContext.Provider value={true}>
          <SidebarComponent {...props} />
        </SidebarReadOnlyContext.Provider>
      ),
      options: {
        variant: 'REGULAR',
        id: node.id,
        containerStyle: {
          '#sidebar-footer': {
            display: 'none',
          },
          [`.${HIDE_CONTAINER_HEADER_CLASSNAME}, .${TEMPLATE_PREVIEW_BUTTON_CLASSNAME}`]: {
            zIndex: 1,
          },
        },
      },
      props: { journeyActionId: node.id },
    });
  }
};
